import { IBaseModel } from "../BaseModel";

export enum TicketStatus {
  Open,
  InProgress,
  Resolved,
  Closed,
}

export enum TicketPriority {
  Low,
  Medium,
  High,
  Urgent,
}

export enum TicketType {
  Bug,
  Feature,
}

/**
 * Internal triage decision on a feature request. Staff only - the API strips it from
 * customer-facing responses, so it exists on the admin dashboard and nowhere else.
 */
export enum TicketFeatureDecision {
  Undecided,
  Implement,
  Backlog,
  No,
}

/**
 * Summary bar chips. Mirrors TicketQuickFilter on the API - values are sent as
 * the enum name, matching how ASP.NET binds enums from the query string.
 */
export type TicketQuickFilter =
  | "Active"
  | "Unassigned"
  | "AwaitingFirstReply"
  | "AwaitingResponse"
  | "CreatedToday"
  | "CreatedThisWeek"
  | "ClosedToday"
  | "ClosedThisWeek";

export interface ITicketUserSummary {
  /** Assignee email, or null/undefined for the unassigned bucket. */
  assignedToUserId?: string;
  /** Active tickets - anything not Resolved or Closed. */
  active: number;
  /** Tickets taken through to Resolved or Closed. */
  done: number;
}

/** Average hours to first staff reply and to resolution. Undefined means no data. */
export interface ITicketAvgResponse {
  firstToday?: number;
  firstThisWeek?: number;
  firstAll?: number;
  resolveToday?: number;
  resolveThisWeek?: number;
  resolveAll?: number;
}

export interface ITicketSummary {
  open: number;
  inProgress: number;
  resolved: number;
  closed: number;
  low: number;
  medium: number;
  high: number;
  urgent: number;
  active: number;
  unassigned: number;
  awaitingFirstReply: number;
  awaitingResponse: number;
  createdToday: number;
  createdThisWeek: number;
  closedToday: number;
  closedThisWeek: number;
  byUser: ITicketUserSummary[];
  avgResponse: ITicketAvgResponse;
}

export interface ITicketNote extends IBaseModel {
  ticketId: string;
  userId: string;
  authorName?: string;
  authorEmail?: string;
  note: string;
  isInternal: boolean;
  /** Admin / portal staff-authored note (Betty customer notes are false). */
  isFromStaff?: boolean;
}

export interface ITicketAttachment extends IBaseModel {
  ticketId: string;
  uploadedByUserId: string;
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface ITicket extends IBaseModel {
  customerId: string;
  createdByUserId: string;
  /** Email of the staff member who created the ticket on behalf of the customer (admin-created tickets only). */
  createdByStaffEmail?: string;
  /** Display name of that staff member captured at creation time. */
  createdByStaffName?: string;
  assignedToUserId?: string;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  type: TicketType;
  /** Internal triage decision, only meaningful when type is Feature. Staff only. */
  featureDecision?: TicketFeatureDecision;
  /** UTC completion time when status is Resolved or Closed (from API). */
  closedAt?: string;
  /** Notes from the customer (not staff). */
  customerNoteCount: number;
  /** False when nobody on staff has replied yet. */
  hasStaffReply: boolean;
  /** True when the customer spoke last. */
  awaitingResponse: boolean;
  notes: ITicketNote[];
  attachments: ITicketAttachment[];
}

export interface IAddTicketNoteRequest {
  note: string;
  isInternal: boolean;
  userId?: string;
}

export interface ICreateTicketRequest {
  customerId: string;
  createdByUserId: string;
  assignedToUserId?: string;
  subject: string;
  description: string;
  priority: TicketPriority;
  type: TicketType;
}

export interface IBulkUpdateTicketStatusRequest {
  ticketIds: string[];
  status: TicketStatus;
}

export interface IMergeTicketsRequest {
  ticketIds: string[];
  /** Blank falls back to the earliest ticket's subject. */
  subject?: string;
}

/**
 * Partial by design - the API leaves any omitted field untouched, so callers
 * only send what they are actually changing.
 */
export interface IUpdateTicketRequest {
  id: string;
  subject?: string;
  description?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  type?: TicketType;
  /** Internal triage decision on a feature request. Staff only. */
  featureDecision?: TicketFeatureDecision;
  assignedToUserId?: string;
}
